import SortOrder from '~/client/src/shared/enums/SortOrder'
import type { IColumnConfig } from '~/client/src/shared/hooks/useColumnConfig'

import type { IViewOptionsPanelProps, IViewOptionsSortValue } from './types'

interface IGroupingOptionInput {
  readonly id: string
  readonly label: string
}

interface ISortingOptionInput {
  readonly id: string
  readonly caption: string
}

interface IGroupingInput {
  value: string | null
  defaultValue?: string | null
  options: readonly IGroupingOptionInput[]
  onChange: (key: string | null) => void
  allowNone?: boolean
}

interface ISortingInput {
  value: string | null
  defaultValue?: IViewOptionsSortValue
  options: readonly ISortingOptionInput[]
  order: SortOrder
  onChange: (optionId: string, order: SortOrder) => void
}

interface IColumnsInput {
  items: readonly IColumnConfig[]
  defaultItems: readonly IColumnConfig[]
  onChange: (
    items: Array<Pick<IColumnConfig, 'dataKey' | 'isFixed' | 'isHidden'>>,
  ) => void
  onReset: () => void
}

interface IBuildViewOptionsPanelPropsParams {
  grouping: IGroupingInput
  sorting: ISortingInput
  columns: IColumnsInput
}

function toColumnItems(columns: readonly IColumnConfig[]) {
  return columns.map(column => ({
    key: column.dataKey,
    label: column.label,
    isFixed: column.isFixed,
    isHidden: column.isHidden,
  }))
}

export function buildViewOptionsPanelProps({
  grouping,
  sorting,
  columns,
}: IBuildViewOptionsPanelPropsParams): IViewOptionsPanelProps {
  return {
    grouping: {
      value: grouping.value,
      options: grouping.options.map(option => ({
        key: option.id,
        label: option.label,
      })),
      onChange: grouping.onChange,
      ...(grouping.defaultValue === undefined
        ? {}
        : { defaultValue: grouping.defaultValue }),
      ...(grouping.allowNone === undefined
        ? {}
        : { allowNone: grouping.allowNone }),
    },
    sorting: {
      value: { key: sorting.value || null, order: sorting.order },
      options: sorting.options.map(option => ({
        key: option.id,
        label: option.caption,
      })),
      ...(sorting.defaultValue === undefined
        ? {}
        : { defaultValue: sorting.defaultValue }),
      onChange: (value: IViewOptionsSortValue) => {
        if (!value.key) {
          sorting.onChange('', SortOrder.DEFAULT)
          return
        }

        sorting.onChange(
          value.key,
          // Picking a column while the order is unset must apply a real
          // direction or the sort would no-op.
          value.order === undefined || value.order === SortOrder.DEFAULT
            ? SortOrder.ASC
            : value.order,
        )
      },
    },
    columns: {
      items: toColumnItems(columns.items),
      defaultItems: toColumnItems(columns.defaultItems),
      onChange: items =>
        columns.onChange(
          items.map(item => ({
            dataKey: item.key,
            isFixed: !!item.isFixed,
            isHidden: !!item.isHidden,
          })),
        ),
      onReset: columns.onReset,
    },
  }
}
